import React, { useState } from 'react';
import { Radio, Stack, Box, NumberInput, Text, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IJournalResponseInput } from '../../../../global/entities/Journal/Journal';

interface YesNoCounterQuestionProps {
    questionId: number;
    response: IJournalResponseInput | undefined;
    onResponseChange: (questionId: number, updates: Partial<IJournalResponseInput>) => void;
    onValidationChange?: (questionId: number, hasError: boolean) => void;
    min?: number;
    max?: number;
    unit?: string;
    placeholder?: string;
    readOnly?: boolean;
}

export const YesNoCounterQuestion: React.FC<YesNoCounterQuestionProps> = ({
    questionId,
    response,
    onResponseChange,
    onValidationChange,
    min = 0,
    max = 999,
    unit = '',
    placeholder,
    readOnly = false
}) => {
    const { t } = useTranslation();
    const [validationError, setValidationError] = useState<string | null>(null);
    
    const primaryResponse = response?.responseValue;
    const counterValue = response?.subResponseValue ? parseFloat(response.subResponseValue) : undefined;
    
    // Normalize the response value to lowercase to match radio button values
    const normalizedValue = primaryResponse?.toLowerCase() || '';
    
    // Validation function for number input
    const handleNumberChange = (value: string | number) => {
        setValidationError(null);
        
        if (value === '' || value === null || value === undefined) {
            setValidationError(' ');
            onValidationChange?.(questionId, true); // Report validation error
            return;
        }
        
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        
        // Valid input
        onValidationChange?.(questionId, false); // Report no validation error
        onResponseChange(questionId, { subResponseValue: numValue.toString() });
    };
    
    return (
        <Stack gap="md">
            {/* Primary yes/no question */}
            <Radio.Group
                value={normalizedValue}
                onChange={readOnly ? undefined : (value) => {
                    onResponseChange(questionId, { 
                        responseValue: value,
                        subResponseValue: value === 'yes' ? '' : ''
                    });
                    
                    // Clear validation errors when switching to "no" or clear the number field
                    if (value === 'no') {
                        setValidationError(null);
                        onValidationChange?.(questionId, false);
                    }
                }}
            >
                <Group gap="md">
                    <Radio 
                        value="yes" 
                        label={t('journal.yes')}
                    />
                    <Radio 
                        value="no" 
                        label={t('journal.no')}
                    />
                </Group>
            </Radio.Group>

            {/* Counter question if yes */}
            {normalizedValue === 'yes' && (
                <Box mt="md" p="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)', borderRadius: 4 }}>
                    <Text size="sm" fw={500} mb="sm">
                        {t('journal.howMany')}
                    </Text>
                    
                    <NumberInput
                        placeholder={placeholder || t('journal.enterNumber')}
                        value={counterValue}
                        onChange={readOnly ? undefined : handleNumberChange}
                        min={min}
                        max={max}
                        suffix={unit ? ` ${unit}` : ''}
                        error={validationError}
                        clampBehavior="strict"
                        inputMode="numeric"
                    />
                </Box>
            )}
        </Stack>
    );
}; 